import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  MdAutoFixHigh,
  MdChat, MdClose, MdFormatQuote,
  MdLabel,
  MdMenuBook,
  MdRefresh
} from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import Metadata from '../Metadata';
import styles from './FeatureRibbon.module.css';

const FeatureRibbon = ({
  // Feature enablement (from ContentOptionsRibbon toggles)
  includeMetadata,
  includeGlossary,
  includeQuote,
  includeCitation,

  // Content data
  metadata,
  onMetadataUpdate,
  onRegenerateMetadata,
  isMetadataLoading,

  glossaryContent,
  onRegenerateGlossary,
  isGlossaryLoading,

  quoteContent,
  onRegenerateQuote,
  isQuoteLoading,

  // Citation
  citationData,
  isCitationLoading,
  onRegenerateCitations,
  activeCitationId,
  onCitationSelect,

  // Suggestions
  onGetSuggestions,
  isSuggestionsLoading,
  suggestions,
  onApplySuggestion,
  applyingSuggestionId,
}) => {
  const [activeTab, setActiveTab] = useState(null);
  const [expandedSuggestionId, setExpandedSuggestionId] = useState(null);

  // Auto-open citation tab when a citation is clicked in the editor
  useEffect(() => {
    if (activeCitationId != null) {
      setActiveTab('citation');
    }
  }, [activeCitationId]);

  const toggleTab = (tab) => {
    setActiveTab(activeTab === tab ? null : tab);
  };

  // Build feature tabs dynamically based on what's enabled
  const tabs = [];

  if (includeMetadata) {
    tabs.push({
      id: 'metadata',
      label: 'Metadata',
      icon: <MdLabel />,
      isLoading: isMetadataLoading,
      hasContent: metadata && (metadata.title || metadata.researchers?.length > 0 || metadata.tags?.length > 0),
    });
  }

  if (includeGlossary) {
    tabs.push({
      id: 'glossary',
      label: 'Glossary',
      icon: <MdMenuBook />,
      isLoading: isGlossaryLoading,
      hasContent: !!glossaryContent,
    });
  }

  if (includeQuote) {
    tabs.push({
      id: 'quote',
      label: 'Quote',
      icon: <MdChat />,
      isLoading: isQuoteLoading,
      hasContent: !!quoteContent,
    });
  }

  if (includeCitation) {
    tabs.push({
      id: 'citation',
      label: 'Citations',
      icon: <MdFormatQuote />,
      isLoading: isCitationLoading,
      hasContent: citationData && citationData.length > 0,
      badge: citationData?.length || null,
    });
  }

  // Suggestions are always shown
  tabs.push({
    id: 'suggestions',
    label: 'Suggestions',
    icon: <MdAutoFixHigh />,
    isLoading: isSuggestionsLoading,
    hasContent: suggestions?.length > 0,
    badge: suggestions?.length || null,
  });

  if (tabs.length === 0) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'metadata':
        return (
          <Metadata
            metadata={metadata}
            onUpdate={onMetadataUpdate}
            onRegenerate={onRegenerateMetadata}
            isLoading={isMetadataLoading}
          />
        );

      case 'glossary':
        return (
          <>
            <div className={styles.citationActions}>
              <span className={styles.citationCount}>Glossary enhancement</span>
              <button
                className={styles.actionBtn}
                onClick={onRegenerateGlossary}
                disabled={isGlossaryLoading}
                title="Regenerate glossary"
              >
                <MdRefresh className={isGlossaryLoading ? styles.spinIcon : ''} />
                Regenerate
              </button>
            </div>
            {isGlossaryLoading && !glossaryContent ? (
              <div className={styles.loadingState}>
                <span className={styles.spinner} />
                <span>Generating glossary...</span>
              </div>
            ) : glossaryContent ? (
              typeof glossaryContent === 'object' && glossaryContent.items ? (
                <div className={styles.glossaryList}>
                  {glossaryContent.items.map((item, index) => (
                    <div key={index} className={styles.glossaryItem}>
                      <span className={styles.glossaryTerm}>{item.term}</span>
                      <span className={styles.glossaryDef}>{item.definition}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.markdownContent}>
                  <ReactMarkdown>{glossaryContent}</ReactMarkdown>
                </div>
              )
            ) : (
              <div className={styles.emptyState}>No glossary yet. Generate a summary with glossary enabled.</div>
            )}
          </>
        );

      case 'quote':
        return (
          <>
            <div className={styles.citationActions}>
              <span className={styles.citationCount}>Quote enhancement</span>
              <button
                className={styles.actionBtn}
                onClick={onRegenerateQuote}
                disabled={isQuoteLoading}
                title="Regenerate quote"
              >
                <MdRefresh className={isQuoteLoading ? styles.spinIcon : ''} />
                Regenerate
              </button>
            </div>
            {isQuoteLoading && !quoteContent ? (
              <div className={styles.loadingState}>
                <span className={styles.spinner} />
                <span>Extracting key quote...</span>
              </div>
            ) : quoteContent ? (
              <blockquote className={styles.quoteBlock}>
                <ReactMarkdown>{quoteContent}</ReactMarkdown>
              </blockquote>
            ) : (
              <div className={styles.emptyState}>No quote yet. Generate a summary with quote enabled.</div>
            )}
          </>
        );

      case 'citation':
        return (
          <>
            <div className={styles.citationActions}>
              <span className={styles.citationCount}>
                {citationData?.length || 0} citation{citationData?.length !== 1 ? 's' : ''}
              </span>
              <button
                className={styles.actionBtn}
                onClick={onRegenerateCitations}
                disabled={isCitationLoading}
                title="Regenerate citations"
              >
                <MdRefresh className={isCitationLoading ? styles.spinIcon : ''} />
                Regenerate
              </button>
            </div>
            {isCitationLoading ? (
              <div className={styles.loadingState}>
                <span className={styles.spinner} />
                <span>Loading citations...</span>
              </div>
            ) : citationData?.length > 0 ? (
              <div className={styles.citationList}>
                {citationData
                  .filter((c) => activeCitationId == null || c.id === activeCitationId)
                  .map((c) => (
                  <div key={c.id} className={styles.citationGroup}>
                    <button
                      className={`${styles.citationItem} ${activeCitationId === c.id ? styles.citationItemActive : ''}`}
                      onClick={() => onCitationSelect?.(activeCitationId === c.id ? null : c.id)}
                    >
                      <span className={styles.citationBadge}>[{c.id}]</span>
                      <span className={styles.citationPreview}>{c.summary_sentence}</span>
                      <span className={styles.citationMeta}>{Math.round(c.similarity * 100)}%</span>
                    </button>
                    {activeCitationId === c.id && (
                      <div className={styles.citationDetail}>
                        <div className={styles.citationSection}>
                          <span className={styles.citationSectionLabel}>Summary</span>
                          <p className={styles.citationSectionText}>{c.summary_sentence}</p>
                        </div>
                        <div className={styles.citationSection}>
                          <span className={styles.citationSectionLabel}>Source</span>
                          <p className={styles.citationSectionText}>{c.source_chunk}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>No citations yet.</div>
            )}
          </>
        );

      case 'suggestions':
        return (
          <>
            <div className={styles.citationActions}>
              <span className={styles.citationCount}>
                {suggestions?.length || 0} suggestion{suggestions?.length !== 1 ? 's' : ''}
              </span>
              <button
                className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                onClick={onGetSuggestions}
                disabled={isSuggestionsLoading}
                title="Regenerate suggestions"
              >
                <MdRefresh className={isSuggestionsLoading ? styles.spinIcon : ''} />
                {suggestions?.length > 0 ? 'Regenerate' : 'Generate'}
              </button>
            </div>
            {isSuggestionsLoading ? (
              <div className={styles.loadingState}>
                <span className={styles.spinner} />
                <span>Analysing your summary...</span>
              </div>
            ) : suggestions?.length > 0 ? (
              <div className={styles.suggestionList}>
                {suggestions.map((s, index) => {
                  const isExpanded = expandedSuggestionId === s.id;
                  const isApplying = applyingSuggestionId === s.id;

                  return (
                    <div
                      key={s.id}
                      className={`${styles.suggestionCard} ${isExpanded ? styles.suggestionCardExpanded : ''} ${isApplying ? styles.suggestionCardApplying : ''}`}
                    >
                      <button
                        className={styles.suggestionCardHeader}
                        onClick={() => setExpandedSuggestionId(isExpanded ? null : s.id)}
                      >
                        <span className={styles.suggestionNumber}>{index + 1}.</span>
                        <div className={styles.suggestionTitleArea}>
                          <span className={styles.suggestionTitle}>{s.title}</span>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className={styles.suggestionCardBody}>
                          <p className={styles.suggestionDesc}>{s.description}</p>
                          {s.target_section && (
                            <span className={styles.suggestionSectionTag}>
                              Section: {s.target_section}
                            </span>
                          )}
                          <button
                            className={`${styles.actionBtn} ${styles.actionBtnPrimary} ${styles.actionBtnFull}`}
                            onClick={() => onApplySuggestion(s)}
                            disabled={isApplying}
                          >
                            {isApplying ? (
                              <><MdRefresh className={styles.spinIcon} /> Applying...</>
                            ) : (
                              <><MdAutoFixHigh /> Apply Suggestion</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyState}>
                No suggestions yet. Click "Generate" to create recommendations.
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`${styles.sidebar} ${activeTab ? styles.expanded : ''}`}>
      {/* Expanded panel content */}
      {activeTab && (
        <div className={styles.panelContent}>
          <div className={styles.panelHeader}>
            <h4 className={styles.panelTitle}>
              {tabs.find(t => t.id === activeTab)?.label}
            </h4>
            <button
              className={styles.closeBtn}
              onClick={() => setActiveTab(null)}
              title="Close"
            >
              <MdClose />
            </button>
          </div>
          <div className={styles.panelBody}>
            {renderTabContent()}
          </div>
        </div>
      )}

      {/* Icon rail */}
      <div className={styles.iconRail}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.railTab} ${activeTab === tab.id ? styles.active : ''} ${tab.hasContent ? styles.hasContent : ''}`}
            onClick={() => toggleTab(tab.id)}
            title={tab.label}
          >
            <span className={styles.railIcon}>{tab.icon}</span>
            <span className={styles.railLabel}>{tab.label}</span>
            {tab.isLoading && <span className={styles.railSpinner} />}
            {!tab.isLoading && tab.badge > 0 && (
              <span className={styles.railBadge}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

FeatureRibbon.propTypes = {
  includeMetadata: PropTypes.bool,
  includeGlossary: PropTypes.bool,
  includeQuote: PropTypes.bool,
  includeCitation: PropTypes.bool,
  metadata: PropTypes.object,
  onMetadataUpdate: PropTypes.func,
  onRegenerateMetadata: PropTypes.func,
  isMetadataLoading: PropTypes.bool,
  glossaryContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onRegenerateGlossary: PropTypes.func,
  isGlossaryLoading: PropTypes.bool,
  quoteContent: PropTypes.string,
  onRegenerateQuote: PropTypes.func,
  isQuoteLoading: PropTypes.bool,
  citationData: PropTypes.array,
  isCitationLoading: PropTypes.bool,
  onRegenerateCitations: PropTypes.func,
  activeCitationId: PropTypes.number,
  onCitationSelect: PropTypes.func,
  onGetSuggestions: PropTypes.func,
  isSuggestionsLoading: PropTypes.bool,
  suggestions: PropTypes.array,
  onApplySuggestion: PropTypes.func,
  applyingSuggestionId: PropTypes.number,
};

export default FeatureRibbon;
